import { formatPeriods } from './formatMonthList';
import { accountKey, groupHeldPeriods } from './periods';
import { accountMaskLabel } from './statementFilings';

import { AccountStatementCoverage, StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

/** One entry of a step's upload list: which card a file was uploaded under. */
export interface CardUpload {
    readonly member_document_id: string;
    readonly file_name: string;
    /** Absent on entries recorded before the card was. */
    readonly account_key?: string;
}

/** A file uploaded under another card that holds months on this one. */
export interface FileUploadedElsewhere {
    readonly documentUuid: string;
    readonly fileName: string;
    /** That card's mask, or undefined when the card is no longer in coverage. */
    readonly uploadedUnderMask: string | undefined;
    readonly periods: StatementPeriod[];
}

interface FilesUploadedElsewhereArgs {
    /** The card being described. */
    readonly account: AccountStatementCoverage;
    /** Every card in coverage, to name the one a file was uploaded under. */
    readonly accounts: AccountStatementCoverage[];
    /** Where each file was uploaded. A file missing here is left out. */
    readonly uploads: readonly CardUpload[];
}

/**
 * Files uploaded under another card that hold months on this one, so the card can say why it's
 * credited for a month the member never uploaded there. One entry per file, in `held_periods`
 * order. Files uploaded under this card are left out because the card already counts them. Months
 * with no document came from the bank, and `groupHeldPeriods` drops them.
 */
export function filesUploadedElsewhere({
    account,
    accounts,
    uploads,
}: FilesUploadedElsewhereArgs): FileUploadedElsewhere[] {
    const thisCard = accountKey(account);
    const uploadByUuid = new Map(uploads.map((upload) => [upload.member_document_id, upload]));
    const maskByAccountKey = new Map(accounts.map((candidate) => [accountKey(candidate), candidate.account_mask]));

    return Object.entries(groupHeldPeriods(account.held_periods)).flatMap(([documentUuid, periods]) => {
        const upload = uploadByUuid.get(documentUuid);
        // No recorded card could mean this one, and "uploaded under another account" would be false.
        if (!upload?.account_key || upload.account_key === thisCard) {
            return [];
        }
        return [
            {
                documentUuid,
                fileName: upload.file_name,
                uploadedUnderMask: maskByAccountKey.get(upload.account_key),
                periods,
            },
        ];
    });
}

/** "July 2026 from combined.pdf, uploaded under ··4034". */
export function uploadedElsewhereLine(file: FileUploadedElsewhere): string {
    const card = accountMaskLabel(file.uploadedUnderMask);
    return `${formatPeriods(file.periods)} from ${file.fileName}, uploaded under ${card}`;
}
